import noUiSlider from 'nouislider';
import {
  MIN_FULL_RANGE_STEP,
  MIN_SQM_RANGE_STEP,
  DEFAULT_FULL_RANGE,
  USD_MIN_FULL_RANGE_STEP,
  USD_DEFAULT_FULL_RANGE,
  USD_MIN_SQM_RANGE_STEP,
  formatNumberTo,
  formatNumberFrom,
  scaleToPrice,
  getValuesLabel,
  getNumber,
  fullRange,
  sqmRange,
} from './rangeHelper';
import { isCurrencyUsd } from '../../helpers/currency';

const ACTIVE_BAR_CLASS = '-active';

class PriceSlider {
  constructor(node, options = {}) {
    this.node = node;
    this.options = options;
    this.currency = null;
    this.setRange();
    this.create();
  }

  setRange() {
    const { min, max } = JSON.parse(this.node.getAttribute('data-slider-range'));
    const [initialFrom, initialTo] = this.node
      .getAttribute('data-slider-initial')
      .split('|')
      .map((p) => Number(p));
    const isFullRange = this.options.isFullPrice;

    this.minUah = getNumber(min, false, isFullRange);
    this.maxUah = getNumber(max, false, isFullRange);
    this.minUsd = getNumber(Math.round(this.minUah / window.currencyRateUSD), true, isFullRange);
    this.maxUsd = getNumber(Math.round(this.maxUah / window.currencyRateUSD), true, isFullRange);
    this.uahRange = isFullRange
      ? fullRange(this.minUah, this.maxUah, DEFAULT_FULL_RANGE, MIN_FULL_RANGE_STEP)
      : sqmRange(this.minUah, this.maxUah, MIN_SQM_RANGE_STEP);
    this.usdRange = isFullRange
      ? fullRange(this.minUsd, this.maxUsd, USD_DEFAULT_FULL_RANGE, USD_MIN_FULL_RANGE_STEP)
      : sqmRange(this.minUsd, this.maxUsd, USD_MIN_SQM_RANGE_STEP);

    this.min = this.minUah;
    this.max = this.maxUah;
    this.initialFrom = initialFrom === min ? this.min : initialFrom;
    this.initialTo = initialTo === max ? this.max : initialTo;
    this.currentMargin = this.uahRange.min[1];
  }

  create() {
    noUiSlider.create(this.node, {
      start: [this.initialFrom, this.initialTo],
      connect: true,
      range: this.uahRange,
      margin: this.currentMargin,
      format: {
        from: (value) => formatNumberFrom(value, this.min, this.max),
        to: formatNumberTo,
      },
    });

    this.node.noUiSlider.on('update', () => {
      this.updateChart(this.node.noUiSlider.getPositions());
      this.options.onUpdate(
        getValuesLabel(this.node.noUiSlider.get(), this.min, this.max, this.currency, this.options.isFullPrice),
      );
    });

    this.node.noUiSlider.on('change', () => {
      const [minValue] = this.node.noUiSlider.get(true);
      this.updateMargin(minValue, true);

      const prices = scaleToPrice(this.node.noUiSlider.get(), this.min, this.max);
      this.options.onChange(prices, this.currency);
    });
  }

  updateChart(positions) {
    if (!this.options.chartNode) return;
    const [leftHandlePosition, rightHandlePosition] = positions;
    const bars = this.options.chartNode.querySelectorAll('[data-slider-chart-bar]');
    const leftBarsMaxIndex = Math.ceil(leftHandlePosition * bars.length / 100);
    const rightBarsMinIndex = Math.floor(rightHandlePosition * bars.length / 100);

    bars.forEach((bar, index) => {
      if (index < leftBarsMaxIndex || index >= rightBarsMinIndex) {
        bar.classList.remove(ACTIVE_BAR_CLASS);
      } else if (!bar.classList.contains(ACTIVE_BAR_CLASS)) {
        bar.classList.add(ACTIVE_BAR_CLASS);
      }
    });
  }

  updateMargin(minValue, triggerOptionsUpdate = false) {
    const range = isCurrencyUsd(this.currency) ? this.usdRange : this.uahRange;
    let margin = range.min[1];

    Object.values(range).forEach(([value, step]) => {
      if (minValue > value && step) {
        margin = step;
      }
    });

    if (triggerOptionsUpdate && margin !== this.currentMargin) {
      this.node.noUiSlider.updateOptions({
        margin,
      }, false);
    }

    this.currentMargin = margin;
  }

  reset() {
    this.node.noUiSlider.set([this.min, this.max]);
  }

  setCurrency(currency, onMount) {
    this.currency = currency;
    let [min, max] = scaleToPrice(this.node.noUiSlider.get(), this.min, this.max);
    if (isCurrencyUsd(this.currency)) {
      this.min = this.minUsd;
      this.max = this.maxUsd;
      min = min !== this.minUah ? Math.round(min / window.currencyRateUSD) : this.min;
      max = max !== this.maxUah ? Math.round(max / window.currencyRateUSD) : this.max;
      this.updateMargin(min);
      this.node.noUiSlider.updateOptions({
        start: [min, max],
        range: this.usdRange,
        margin: this.currentMargin,
      }, false);
    } else if (!onMount) {
      this.min = this.minUah;
      this.max = this.maxUah;
      min = min !== this.minUsd ? Math.round(min * window.currencyRateUSD) : this.min;
      max = max !== this.maxUsd ? Math.round(max * window.currencyRateUSD) : this.max;
      this.updateMargin(min);
      this.node.noUiSlider.updateOptions({
        start: [min, max],
        range: this.uahRange,
        margin: this.currentMargin,
      }, false);
    }
  }
}

export default PriceSlider;
